import { cn } from '../helpers/index.js'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'
import { convertLexicalToHTML } from '@payloadcms/richtext-lexical/html'
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical'

const headingVariants = cva('text-balance leading-tight tracking-[-0.04em]', {
  variants: {
    size: {
      none: 'tracking-[-0.04em]',
      1: 'lg-desktop:text-[16px] text-[14px] leading-[120%]',
      2: 'lg-desktop:text-[24px] text-[20px] leading-[120%]',
      3: 'desktop:text-[32px] text-[24px] leading-[120%]',
      4: 'desktop:text-[40px] lg-desktop:text-[48px] text-[32px] leading-[100%]',
      5: 'desktop:text-[56px] lg-desktop:text-[64px] text-[40px] leading-[100%]',
    },
    weight: {
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extraBold: 'font-extrabold',
      black: 'font-black',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
  },
  defaultVariants: {
    size: 'none',
    weight: 'bold',
  },
})

type HeadingElement = React.ElementRef<'h1'>

interface HeadingProps extends React.ComponentPropsWithoutRef<'h1'>, VariantProps<typeof headingVariants> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  asChild?: boolean
  color?: string
  html?: string | SerializedEditorState<SerializedLexicalNode>
}

/**
 * Heading - 标题组件
 *
 * @description 用于显示各级标题的组件，支持响应式尺寸、字重、对齐方式和自定义颜色
 *
 * @param {object} props - 组件属性
 * @param {1|2|3|4|5|"none"} props.size - 标题尺寸（响应式）
 * - `1`: 14px → 16px (四级标题，适用于卡片标题、辅助标题)
 * - `2`: 20px → 24px (三级标题，适用于章节标题)
 * - `3`: 24px → 32px (二级标题，适用于页面子标题)
 * - `4`: 32px → 40px → 48px (一级标题，适用于页面主标题)
 * - `5`: 40px → 56px → 64px (特大标题，适用于首屏标题、横幅标题)
 * - `none`: 无预设大小，继承父级样式
 * @param {"medium"|"semibold"|"bold"|"extraBold"|"black"} props.weight - 字重，默认 bold
 * @param {"left"|"center"|"right"} props.align - 文字对齐方式
 * @param {"h1"|"h2"|"h3"|"h4"|"h5"|"h6"} props.as - 渲染的标题标签，默认 h2
 * @param {boolean} props.asChild - 是否作为子组件渲染
 * @param {string} props.color - 自定义文字颜色
 * @param {string|SerializedEditorState} props.html - HTML 内容或富文本编辑器状态
 *
 * @example
 * ```tsx
 * // 基础用法 - 页面主标题
 * <Heading as="h1" size={4}>欢迎使用我们的产品</Heading>
 *
 * // 章节标题
 * <Heading as="h2" size={3}>功能特性</Heading>
 *
 * // 卡片标题
 * <Heading as="h3" size={2}>产品介绍</Heading>
 *
 * // 居中对齐的大标题
 * <Heading as="h1" size={5} align="center">品牌故事</Heading>
 *
 * // 自定义颜色和字重
 * <Heading size={3} weight="semibold" color="#ff6b6b">
 *   特别公告
 * </Heading>
 *
 * // 使用 HTML 内容
 * <Heading size={2} html="<strong>重要</strong>通知" />
 * ```
 *
 * @note 尺寸会根据屏幕断点自动调整：
 * - 移动端：基础尺寸
 * - 桌面端 (≥1440px)：较大尺寸
 * - 大屏端 (≥1920px)：最大尺寸
 */
const Heading = React.forwardRef<HeadingElement, HeadingProps>((props, forwardedRef) => {
  const {
    children,
    className,
    size = 'none', // 为了兼容旧版本样式，默认值为 none，没有默认的字体大小
    align,
    weight,
    asChild,
    as: Tag = 'h2',
    color,
    html,
    ...headingProps
  } = props

  const htmlContentProps = html
    ? {
        dangerouslySetInnerHTML: { __html: typeof html === 'string' ? html : convertLexicalToHTML({ data: html }) },
      }
    : null

  const styleProps = color
    ? {
        style: {
          color,
        },
      }
    : null
  return (
    <Slot
      {...headingProps}
      ref={forwardedRef}
      className={cn(
        headingVariants({
          size,
          weight,
          align,
        }),
        className
      )}
      {...htmlContentProps}
      {...styleProps}
    >
      {asChild ? children : <Tag>{htmlContentProps ? null : children}</Tag>}
    </Slot>
  )
})

Heading.displayName = 'Heading'

export { Heading }
export type { HeadingProps }
